import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useVideoQueueStore } from "@/common/store/videoQueueStore";

interface UsePlayerObserverProp {
  playerWrapRef: React.RefObject<HTMLDivElement | null>;
  position: number;
  gifId: string;
}

const FIRST_GIF_THRESHOLD = 0.9;

export const usePlayerObserver = ({
  playerWrapRef,
  position,
  gifId
}: UsePlayerObserverProp) => {
  const [isActive, setIsActive] = useState(false);

  const location = useLocation();
  const videoQueue = useVideoQueueStore((state) => state.videoQueue);
  const addToQueue = useVideoQueueStore((state) => state.addToQueue);
  const removeFromQueue = useVideoQueueStore((state) => state.removeFromQueue);
  const getLastVisibleVideo = useVideoQueueStore((state) => state.getLastVisibleVideo);
  const isFirstOnWatchPage = useMemo(() => location.pathname.includes("/watch") && position === 1, [location, position]);

  useEffect(() => {
    if (!playerWrapRef.current) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsActive(entry.isIntersecting);
      });
    });

    observer.observe(playerWrapRef.current);

    return () => {
      observer.disconnect();
    };
  }, [playerWrapRef]);

  useEffect(() => {
    if (!playerWrapRef.current) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          addToQueue(gifId);
        } else if (videoQueue.includes(gifId)) {
          removeFromQueue(gifId);
        }
      });
    }, {
      threshold: isFirstOnWatchPage ? FIRST_GIF_THRESHOLD : 0,
      rootMargin: isFirstOnWatchPage ? "0px 0px 0px 0px" : "-50% 0px -50% 0px"
    });

    observer.observe(playerWrapRef.current);

    return () => {
      observer.disconnect();
    };
  }, [playerWrapRef, isFirstOnWatchPage, videoQueue, gifId]);

  const isVisible = useMemo(() => getLastVisibleVideo() === gifId, [videoQueue, gifId]);

  return {
    isVisible,
    isActive
  };
};
